<template>
  <div class="page-wrap" id="client-location">

    <div class="panel-card">

      <!-- ── Header ── -->
      <div class="panel-header">
        <div class="panel-header-left">
          <div class="panel-icon"><i class="fa fa-map-marker-alt"></i></div>
          <div>
            <div class="panel-title">{{ isEdit ? 'Edit Location' : 'Create Location' }}</div>
            <div class="panel-sub">{{ isEdit ? 'Update location details' : 'Fill in the details to create a new location' }}</div>
          </div>
        </div>
        <div class="panel-header-right">
          <button class="btn-back" @click="goBack()">
            <i class="fa fa-arrow-left"></i> Back
          </button>
          <button
            v-if="!isEdit"
            class="btn-save-alt"
            @click="store('SAA', 'kt_save_and_add_service')"
            ref="kt_save_and_add_service"
          >
            <i class="fa fa-plus"></i> Save &amp; Add Another
          </button>
          <button
            v-if="isEdit"
            class="btn-save"
            @click="update('kt_update_service')"
            ref="kt_update_service"
          >
            <i class="fa fa-check"></i> Update
          </button>
          <button
            v-if="!isEdit"
            class="btn-save"
            @click="store('S', 'kt_save_service')"
            ref="kt_save_service"
          >
            <i class="fa fa-check"></i> Save
          </button>
        </div>
      </div>

      <!-- ── Two-column body ── -->
      <div class="form-body">

        <!-- Left column: Location Details -->
        <div class="form-col">

          <!-- Service Provider (admin only) -->
          <div class="section-head" v-if="currentUser.u.is_admin">
            <i class="fa fa-building"></i> Service Provider
          </div>
          <div class="fields-block" v-if="currentUser.u.is_admin">
            <div class="field-group">
              <label class="field-label">Service Provider <span class="req">*</span></label>
              <select
                class="field-select"
                :class="{ 'has-error': errors['service_provider'] }"
                v-model="service_provider"
                @change="getServiceData()"
              >
                <option value="">Select a service provider</option>
                <option
                  v-for="(citem, index) in getserviceProviders"
                  :key="index"
                  :value="citem.id"
                >{{ citem.name }}</option>
              </select>
              <span class="field-error" v-if="errors['service_provider']">{{ errors['service_provider'][0] }}</span>
            </div>
          </div>

          <!-- Location Info -->
          <div class="section-head">
            <i class="fa fa-info-circle"></i> Location Information
          </div>
          <div class="fields-block">
            <div class="field-row">
              <div class="field-group" style="flex:0 0 140px;">
                <label class="field-label">Code <span class="req">*</span></label>
                <input class="field-input" :class="{ 'has-error': errors['slug'] }" type="text" v-model="formData.slug" placeholder="e.g. loc-01" />
                <span class="field-error" v-if="errors['slug']">{{ errors['slug'][0] }}</span>
              </div>
              <div class="field-group" style="flex:1;">
                <label class="field-label">Name <span class="req">*</span></label>
                <input class="field-input" :class="{ 'has-error': errors['name'] }" type="text" v-model="formData.name" placeholder="Location name" />
                <span class="field-error" v-if="errors['name']">{{ errors['name'][0] }}</span>
              </div>
            </div>
            <div class="field-group">
              <label class="field-label">Description <span class="req">*</span></label>
              <textarea class="field-input field-textarea" :class="{ 'has-error': errors['description'] }" v-model="formData.description" placeholder="Brief description" rows="2"></textarea>
              <span class="field-error" v-if="errors['description']">{{ errors['description'][0] }}</span>
            </div>
            <div class="field-row">
              <div class="field-group">
                <label class="field-label">QuickBooks Display Name <span class="req">*</span></label>
                <input class="field-input" :class="{ 'has-error': errors['quickbook_display_name'] }" type="text" v-model="formData.quickbook_display_name" placeholder="QB name" />
                <span class="field-error" v-if="errors['quickbook_display_name']">{{ errors['quickbook_display_name'][0] }}</span>
              </div>
              <div class="field-group">
                <label class="field-label">Display Name for Scheduler</label>
                <input class="field-input" :class="{ 'has-error': errors['display_name'] }" type="text" v-model="formData.display_name" placeholder="Scheduler name" />
                <span class="field-error" v-if="errors['display_name']">{{ errors['display_name'][0] }}</span>
              </div>
            </div>
          </div>

          <!-- Address -->
          <div class="section-head">
            <i class="fa fa-map-pin"></i> Address
          </div>
          <div class="fields-block">
            <div class="field-row">
              <div class="field-group">
                <label class="field-label">Address Line 1 <span class="req">*</span></label>
                <input class="field-input" :class="{ 'has-error': errors['address'] }" type="text" v-model="formData.address" placeholder="Street address" />
                <span class="field-error" v-if="errors['address']">{{ errors['address'][0] }}</span>
              </div>
              <div class="field-group">
                <label class="field-label">Address Line 2</label>
                <input class="field-input" type="text" v-model="formData.address2" placeholder="Apt, suite, etc." />
              </div>
            </div>
            <div class="field-row">
              <div class="field-group">
                <label class="field-label">State <span class="req">*</span></label>
                <input class="field-input" :class="{ 'has-error': errors['state'] }" type="text" v-model="formData.state" placeholder="State" />
                <span class="field-error" v-if="errors['state']">{{ errors['state'][0] }}</span>
              </div>
              <div class="field-group">
                <label class="field-label">City <span class="req">*</span></label>
                <input class="field-input" :class="{ 'has-error': errors['city_name'] }" type="text" v-model="formData.city_name" placeholder="City" />
                <span class="field-error" v-if="errors['city_name']">{{ errors['city_name'][0] }}</span>
              </div>
            </div>
            <div class="field-row">
              <div class="field-group" style="flex:0 0 120px;">
                <label class="field-label">Zip Code <span class="req">*</span></label>
                <input class="field-input" :class="{ 'has-error': errors['zipcode'] }" type="text" v-mask="'#####'" v-model="formData.zipcode" placeholder="12345" />
                <span class="field-error" v-if="errors['zipcode']">{{ errors['zipcode'][0] }}</span>
              </div>
              <div class="field-group" style="flex:1;">
                <label class="field-label">Timezone <span class="req">*</span></label>
                <select class="field-select" :class="{ 'has-error': errors['client_timezone'] }" v-model="formData.client_timezone">
                  <option value="">Select timezone</option>
                  <option v-for="(item, index) in timeZones" :key="index" :value="item">{{ item }}</option>
                </select>
                <span class="field-error" v-if="errors['client_timezone']">{{ errors['client_timezone'][0] }}</span>
              </div>
            </div>
            <div class="field-row">
              <div class="field-group">
                <label class="field-label">County <span class="req">*</span></label>
                <select class="field-select" :class="{ 'has-error': errors['county'] }" v-model="formData.county">
                  <option value="">Select county</option>
                  <option value="Alachua">Alachua</option>
                  <option value="Baker">Baker</option>
                  <option value="Bay">Bay</option>
                  <option value="Bradford">Bradford</option>
                  <option value="Brevard">Brevard</option>
                  <option value="Broward">Broward</option>
                  <option value="Calhoun">Calhoun</option>
                  <option value="Charlotte">Charlotte</option>
                  <option value="Citrus">Citrus</option>
                  <option value="Clay">Clay</option>
                  <option value="Collier">Collier</option>
                  <option value="Columbia">Columbia</option>
                  <option value="Desoto">Desoto</option>
                  <option value="Dixie">Dixie</option>
                  <option value="Duval">Duval</option>
                  <option value="Escambia">Escambia</option>
                  <option value="Flagler">Flagler</option>
                  <option value="Franklin">Franklin</option>
                  <option value="Gadsden">Gadsden</option>
                  <option value="Georgia">Georgia</option>
                  <option value="Gilchrist">Gilchrist</option>
                  <option value="Glades">Glades</option>
                  <option value="Gulf">Gulf</option>
                  <option value="Hamilton">Hamilton</option>
                  <option value="Hardee">Hardee</option>
                  <option value="Hendry">Hendry</option>
                  <option value="Hernando">Hernando</option>
                  <option value="Highlands">Highlands</option>
                  <option value="Hillsborough">Hillsborough</option>
                  <option value="Holmes">Holmes</option>
                  <option value="Indian River">Indian River</option>
                  <option value="Jackson">Jackson</option>
                  <option value="Jefferson">Jefferson</option>
                  <option value="Lafayette">Lafayette</option>
                  <option value="Lake">Lake</option>
                  <option value="Lee">Lee</option>
                  <option value="Leon">Leon</option>
                  <option value="Levy">Levy</option>
                  <option value="Liberty">Liberty</option>
                  <option value="Madison">Madison</option>
                  <option value="Manatee">Manatee</option>
                  <option value="Marion">Marion</option>
                  <option value="Martin">Martin</option>
                  <option value="Miami‐Dade">Miami‐Dade</option>
                  <option value="Monroe">Monroe</option>
                  <option value="Nassau">Nassau</option>
                  <option value="Okaloosa">Okaloosa</option>
                  <option value="Okeechobee">Okeechobee</option>
                  <option value="Orange">Orange</option>
                  <option value="Osceola">Osceola</option>
                  <option value="Palm Beach">Palm Beach</option>
                  <option value="Pasco">Pasco</option>
                  <option value="Pinellas">Pinellas</option>
                  <option value="Polk">Polk</option>
                  <option value="Putnam">Putnam</option>
                  <option value="Santa Rosa">Santa Rosa</option>
                  <option value="Sarasota">Sarasota</option>
                  <option value="Seminole">Seminole</option>
                  <option value="St. Johns">St. Johns</option>
                  <option value="St. Lucie">St. Lucie</option>
                  <option value="Sumter">Sumter</option>
                  <option value="Suwannee">Suwannee</option>
                  <option value="Taylor">Taylor</option>
                  <option value="Union">Union</option>
                  <option value="Volusia">Volusia</option>
                  <option value="Wakulla">Wakulla</option>
                  <option value="Walton">Walton</option>
                  <option value="Washington">Washington</option>
                  <option value="Other States">Other States</option>
                </select>
                <span class="field-error" v-if="errors['county']">{{ errors['county'][0] }}</span>
              </div>
              <div class="field-group">
                <label class="field-label">Contact Number <span class="req">*</span></label>
                <input class="field-input" :class="{ 'has-error': errors['phone_number'] }" type="text" v-mask="'(###)-###-####'" v-model="formData.phone_number" placeholder="(555)-555-5555" />
                <span class="field-error" v-if="errors['phone_number']">{{ errors['phone_number'][0] }}</span>
              </div>
              <div class="field-group">
                <label class="field-label">Show Contact</label>
                <input class="field-input" type="text" v-mask="'(###)-###-####'" v-model="formData.show_contact" placeholder="(555)-555-5555" />
              </div>
            </div>
          </div>

          <!-- Settings -->
          <div class="section-head">
            <i class="fa fa-sliders-h"></i> Settings
          </div>
          <div class="fields-block">
            <div class="toggle-grid">
              <label class="toggle-item">
                <span class="toggle-wrap">
                  <input type="checkbox" v-model="is_active" class="toggle-input" />
                  <span class="toggle-slider"></span>
                </span>
                <span class="toggle-label">Active</span>
              </label>
              <label class="toggle-item">
                <span class="toggle-wrap">
                  <input type="checkbox" v-model="formData.enable_on_site_payment" class="toggle-input" />
                  <span class="toggle-slider"></span>
                </span>
                <span class="toggle-label">On-Site Payment</span>
              </label>
              <label class="toggle-item">
                <span class="toggle-wrap">
                  <input type="checkbox" v-model="formData.is_mobile_fingerprinting" class="toggle-input" />
                  <span class="toggle-slider"></span>
                </span>
                <span class="toggle-label">Mobile Location</span>
              </label>
              <label class="toggle-item">
                <span class="toggle-wrap">
                  <input type="checkbox" v-model="formData.card_scan" class="toggle-input" />
                  <span class="toggle-slider"></span>
                </span>
                <span class="toggle-label">Card Scan</span>
              </label>
              <label class="toggle-item">
                <span class="toggle-wrap">
                  <input type="checkbox" v-model="formData.photo_enabled" class="toggle-input" />
                  <span class="toggle-slider"></span>
                </span>
                <span class="toggle-label">Photo Enabled</span>
              </label>
              <label class="toggle-item">
                <span class="toggle-wrap">
                  <input type="checkbox" v-model="formData.review_enabled" class="toggle-input" />
                  <span class="toggle-slider"></span>
                </span>
                <span class="toggle-label">Feedback Survey</span>
              </label>
              <label class="toggle-item">
                <span class="toggle-wrap">
                  <input type="checkbox" v-model="formData.is_offline_mode" class="toggle-input" />
                  <span class="toggle-slider"></span>
                </span>
                <span class="toggle-label">Offline Mode</span>
              </label>
            </div>
          </div>

          <!-- Image Upload -->
          <div class="section-head">
            <i class="fa fa-image"></i> Location Image
          </div>
          <div class="fields-block">
            <div class="upload-area" :class="{ 'has-image': showImage }">
              <canvas v-if="showImage" id="imageCanvas" ref="imageCanvas" class="upload-preview"></canvas>
              <div v-else class="upload-placeholder">
                <i class="fa fa-cloud-upload-alt"></i>
                <span>Click to upload image</span>
              </div>
              <label class="upload-btn">
                <input type="file" id="imageLoader" @change="updateCanvasImage" hidden />
                <i class="fa fa-upload"></i>
                {{ showImage ? 'Change Image' : 'Upload Image' }}
              </label>
            </div>
            <span class="field-error" v-if="errors['image']">{{ errors['image'][0] }}</span>
          </div>

        </div>

        <!-- Right column: Map + User -->
        <div class="form-col form-col-right">

          <div class="section-head">
            <i class="fa fa-map"></i> Select Location on Map
          </div>
          <div class="fields-block">
            <div class="map-wrap">
              <keep-alive>
                <GoogleMap
                  ref="map"
                  @getFullAddress="setFullAddress"
                  @dragPointer="setLocation"
                  @onSearchClick="setLocation"
                ></GoogleMap>
              </keep-alive>
              <span class="field-error" v-if="errors['long']">Please select a location on the map</span>
            </div>
            <div class="field-group" style="margin-top:12px;">
              <label class="field-label">Google Business URL</label>
              <input class="field-input" type="url" v-model="formData.google_business_url" placeholder="https://..." :class="{ 'has-error': errors['google_business_url'] }" />
              <span class="field-error" v-if="errors['google_business_url']">{{ errors['google_business_url'][0] }}</span>
            </div>
          </div>

          <div class="section-head">
            <i class="fa fa-user"></i> User Information
          </div>
          <div class="fields-block">
            <div class="field-row">
              <div class="field-group">
                <label class="field-label">First Name <span class="req">*</span></label>
                <input class="field-input" :class="{ 'has-error': errors['first_name'] }" type="text" v-model="formData.first_name" placeholder="First name" />
                <span class="field-error" v-if="errors['first_name']">{{ errors['first_name'][0] }}</span>
              </div>
              <div class="field-group">
                <label class="field-label">Last Name <span class="req">*</span></label>
                <input class="field-input" :class="{ 'has-error': errors['last_name'] }" type="text" v-model="formData.last_name" placeholder="Last name" />
                <span class="field-error" v-if="errors['last_name']">{{ errors['last_name'][0] }}</span>
              </div>
            </div>
            <div class="field-group">
              <label class="field-label">Email <span class="req">*</span></label>
              <input class="field-input" :class="{ 'has-error': errors['email'] }" type="email" v-model="formData.email" placeholder="email@example.com" />
              <span class="field-error" v-if="errors['email']">{{ errors['email'][0] }}</span>
            </div>
            <div class="field-group">
              <label class="field-label">Notification Email <span class="req">*</span></label>
              <input class="field-input" :class="{ 'has-error': errors['notification_email'] }" type="text" v-model="formData.notification_email" placeholder="notifications@example.com" />
              <span class="field-error" v-if="errors['notification_email']">{{ errors['notification_email'][0] }}</span>
            </div>
          </div>

        </div>
      </div>

      <!-- ── Service List (full width) ── -->
      <div v-if="loading_data">
        <div class="section-head section-head-full">
          <i class="fa fa-th-list"></i> Service List
          <span class="svc-error" v-if="serviceError">{{ serviceError }}</span>
        </div>
        <div class="svc-table-wrap">
          <table class="svc-table">
            <thead>
              <tr class="svc-group-row">
                <th rowspan="2" style="width:36px;">
                  <input type="checkbox" v-model="selectAll" @change="selectAllServices()" class="svc-checkbox" />
                </th>
                <th rowspan="2" style="min-width:200px; text-align:left;">Service</th>
                <th colspan="5" class="group-header default-group">Default Rates</th>
                <th colspan="5" class="group-header custom-group">Custom Rates</th>
              </tr>
              <tr>
                <th class="svc-th default-col">Rate</th>
                <th class="svc-th default-col">Add. Rate</th>
                <th class="svc-th default-col">Exp. Rate</th>
                <th class="svc-th default-col">Tax %</th>
                <th class="svc-th default-col">Comm.</th>
                <th class="svc-th custom-col">Rate</th>
                <th class="svc-th custom-col">Add. Rate</th>
                <th class="svc-th custom-col">Exp. Rate</th>
                <th class="svc-th custom-col">Tax %</th>
                <th class="svc-th custom-col">Comm.</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(service, index) in service_table_data" :key="index" :class="{ 'svc-row-checked': service.checked }">
                <td style="text-align:center;">
                  <input type="checkbox" v-model="service.checked" class="svc-checkbox" />
                </td>
                <td class="svc-name">{{ service.service }}</td>
                <td class="svc-default">{{ service.default_rate }}</td>
                <td class="svc-default">{{ service.default_additional_rate }}</td>
                <td class="svc-default">{{ service.default_expedited_rate }}</td>
                <td class="svc-default">{{ service.default_tax }}</td>
                <td class="svc-default">{{ service.default_commission }}</td>
                <td>
                  <input type="number" min="0" v-model="service.rate" class="svc-input" />
                </td>
                <td>
                  <input v-if="service.has_additional_rate" type="text" v-model="service.additional_rate" class="svc-input" />
                </td>
                <td>
                  <input v-if="service.service_code == 'apostille'" type="text" v-model="service.expedited_rate" class="svc-input" />
                </td>
                <td>
                  <input type="number" min="0" v-model="service.tax_percentage" class="svc-input" />
                </td>
                <td>
                  <input type="number" min="0" v-model="service.commission_percentage" class="svc-input" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div v-else class="no-service">
        <i class="fa fa-info-circle"></i> No services available
      </div>

    </div>
  </div>
</template>

<script>
import GoogleMap from "@/components/GoogleMap.vue";
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import {
  FETCH_B_SERVICES,
  STORE_B_LOCATION,
  GET_SERVICE_PROVIDER_LIST,
} from "@/core/services/store/actions.type";
import { mapGetters } from "vuex";
export default {
  data() {
    return {
      isEdit: false,
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Locations", route: "client.location" },
        { id: 3, title: "Create New Location", route: "" },
      ],
      service_provider: "",
      service_name: "",
      description: "",
      service_type: "",
      formData: {
        name: "",
        description: "",
        display_name: "",
        address: "",
        address2: "",
        state: "",
        city_name: "",
        zipcode: "",
        long: "",
        lat: "",
        client_timezone: "",
        first_name: "",
        last_name: "",
        email: "",
        username: "",
        slug: "",
        notification_email: "",
        is_mobile_fingerprinting: false,
        enable_on_site_payment: true,
        phone_number: "",
        show_contact: "",
        card_scan: false,
        photo_enabled: false,
        review_enabled: false,
        is_offline_mode: false,
        county: "",
        quickbook_display_name: "",
        google_business_url: "",
        user_name: "",
      },
      service_table_data: [],
      is_active: true,
      showImage: false,
      image_base64: "",
      loading_data: true,
      errors: [],
      serviceError: "",
      selectAll: false,
      serchInput: "",
      timeZones: [],
      image_crop_base64: "",
      regEmail:
        /^(([^<>()\]\\.,;:\s@"]+(\.[^<>()\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/,
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "getserviceProviders",
      "getBServices",
      "getAspectheight",
      "getAspectwidth",
      "getCanvasheight",
    ]),
    filteredServices() {
      let tempService = this.getBServices.data;
      if (this.searchInput != "" && this.searchInput) {
        tempService = tempService.filter((item) =>
          item.name.toUpperCase().includes(this.searchInput.toUpperCase())
        );
      }
      return tempService;
    },
  },
  components: { GoogleMap },
  created() {
    if (this.currentUser.u.is_admin) {
      this.getServiceProvider();
    }
    this.timeZones = this.$moment.tz.names();
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.isEdit = this.$route.name == "client.service.edit" ? true : false;
    if (this.isEdit) {
      this.breadCrumbs[2].title = "Edit | Name of Location";
    }
    if (!this.currentUser.u.is_admin) {
      this.getServiceData();
    }
  },
  watch: {
    getBServices: {
      handler(val) {
        if (val.data && val.data.length > 0) {
          this.service_table_data = [];
          val.data.map((service) => {
            this.service_table_data.push({
              id: service.id,
              service: service.name,
              service_code: service.service_type_code,
              rate: "",
              has_additional_rate: service.has_additional_rate,
              additional_rate: "",
              tax_percentage: "",
              commission_percentage: "",
              expedited_rate: "",
              checked: false,
              default_rate: service.default_rate,
              default_additional_rate: service.additional_rate,
              default_tax: service.tax_percentage,
              default_commission: service.commission_percentage,
              default_expedited_rate: service.default_expedited_rate,
            });
          });
        }
      },
    },
  },
  methods: {
    setFullAddress(data) {
      this.formData.address = data;
    },
    setLocationDragged(location) {
      this.formData.long = location.lng;
      this.formData.lat = location.lat;
    },
    setLocation(location) {
      this.formData.long = location.lng;
      this.formData.lat = location.lat;
      this.formData.city_name = location.city.long_name;
      this.formData.address = location.formatted_address;
      this.formData.zipcode = location.postal_code.long_name;
      let state = location.state.short_name.toUpperCase();
      this.formData.client_timezone = "";
      this.formData.state = state === undefined ? "" : state;
    },
    goBack() {
      var client_slug = this.$route.params.client_slug;
      this.$router.push({ name: "client.location", params: { client_slug } });
    },
    selectAllServices() {
      if (this.selectAll) {
        this.service_table_data.filter((service) => (service.checked = true));
      } else {
        this.service_table_data.filter((service) => (service.checked = false));
      }
    },
    validateEmail(value) {
      if (this.regEmail.test(value)) {
        this.errors["email"] = "";
      } else {
        this.errors["email"] = { 0: "Please enter a valid email address" };
      }
    },
    updateEmail(e) {
      this.validateEmail(e.target.value);
    },
    closeBtnLoad(ref) {
      const submitButton = this.$refs[ref];
      submitButton.classList.remove("spinner", "spinner-light", "spinner-right");
      submitButton.disabled = false;
    },
    setClientSlug(e) {
      var value = e.target.value;
      this.formData.slug = value.replace(/\s+/g, "").toLowerCase();
    },
    loadingButton(ref) {
      const submitButton = this.$refs[ref];
      submitButton.disabled = true;
      submitButton.classList.add("spinner", "spinner-light", "spinner-right");
    },
    clearForm() {
      this.service_table_data = [];
      this.service_provider = "";
      this.formData.name = "";
      this.formData.description = "";
      this.formData.display_name = "";
      this.formData.address = "";
      this.formData.city_name = "";
      this.formData.zipcode = "";
      this.formData.long = "";
      this.formData.lat = "";
      this.formData.client_timezone = "";
      this.formData.first_name = "";
      this.formData.last_name = "";
      this.formData.email = "";
      this.formData.username = "";
      this.formData.slug = "";
      this.formData.address2 = "";
      this.formData.state = "";
      this.formData.notification_email = "";
      this.formData.image = "";
      this.formData.enable_on_site_payment = true;
      this.formData.is_mobile_fingerprinting = false;
      this.formData.card_scan = false;
      this.formData.photo_enabled = false;
      this.formData.review_enabled = false;
      this.formData.county = "";
      this.is_active = true;
      this.showImage = false;
      this.loading_data = true;
      this.image_base64 = "";
      this.serviceError = "";
      this.quickbook_display_name = "";
      this.google_business_url = "";
      this.errors = [];
      this.getServiceData();
    },
    changeImage(val) {
      this.image_crop_base64 = val;
    },
    store(saveType, ref) {
      var service_table_data_ = this.service_table_data.filter(
        (service) => service.checked == true
      );
      if (service_table_data_.length == 0) {
        if (this.currentUser.u.is_admin && this.service_provider == "") {
          this.$toastr.e("Please select service provider");
        } else if (this.currentUser.u.is_admin && this.service_provider) {
          this.$toastr.e("Please select at least one service");
        } else if (!this.currentUser.u.is_admin) {
          this.$toastr.e("Please select at least one service");
        }
      } else {
        this.errors = [];
        this.loadingButton(ref);
        this.formData.image = this.image_base64;
        this.formData.user_name =
          this.formData.first_name + " " + this.formData.last_name;
        let _data = {};
        this.formData.username = this.formData.email;
        this.formData.notification_email =
          this.formData.notification_email != ""
            ? this.formData.notification_email.replace(" ", "")
            : "";
        _data.user_detail = this.formData;
        _data.user_detail.slug = _data.user_detail.slug.replace(/\s/g, "");
        _data.service_provider = this.service_provider;
        _data.rate_list = service_table_data_;
        _data.is_active = this.is_active;
        _data.client_slug = this.$route.params.client_slug;

        this.$store
          .dispatch(STORE_B_LOCATION, _data)
          .then((data) => {
            this.$toastr.s(data.msg);
            this.closeBtnLoad(ref);
            if (saveType == "S") {
              this.$router.push({ name: "client.location" });
            } else if (saveType == "SAA") {
              this.clearForm();
            }
          })
          .catch((err) => {
            this.errors = err;
            this.closeBtnLoad(ref);
          });
      }
    },
    update(ref) {
      this.loadingButton(ref);
      alert("Update");
      this.$router.push({ name: "client.location" });
    },
    getServiceData() {
      var data = { status: true, service_provider: this.service_provider };
      this.loadingData = true;
      this.$store
        .dispatch(FETCH_B_SERVICES, data)
        .then(() => { this.loadingData = false; })
        .catch(() => { this.loadingData = false; });
    },
    updateCanvasImage(e) {
      this.showImage = true;
      var self = this;
      var reader, files = e.target.files;
      reader = new FileReader();
      reader.onload = (e) => {
        this.e = e;
        var img = new Image();
        img.onload = function () { self.drawCanvasImage(img); };
        img.src = e.target.result;
      };
      reader.readAsDataURL(files[0]);
    },
    drawCanvasImage(imageObj) {
      var canvas = this.$refs.imageCanvas;
      canvas.width = 120;
      canvas.height = (100 * imageObj.height) / imageObj.width;
      var context = canvas.getContext("2d");
      context.drawImage(imageObj, 0, 0, 120, (100 * imageObj.height) / imageObj.width);
      var base64 = canvas.toDataURL("image/png");
      this.image_base64 = base64;
    },
    getServiceProvider() {
      this.loadingData = true;
      this.$store
        .dispatch(GET_SERVICE_PROVIDER_LIST)
        .then(res => {
          if (res.code == 200) this.url = res.url;
          this.loadingData = false;
        })
        .catch(err => {
          this.loadingData = false;
          this.msg = err.error;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
$font: 'Segoe UI', sans-serif;
$blue: #389ad4;
$dark: #0d1b3e;
$border: #dde2ec;
$label-color: #7a8499;
$error: #e53e3e;
$green: #276749;

.page-wrap {
  padding: 8px 0 80px;
  font-family: $font;
}

/* ── Panel ── */
.panel-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(13, 27, 62, 0.08);
  overflow: clip;
}

/* ── Header ── */
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #fafbfd;
  border-bottom: 1px solid #eef0f6;
  flex-wrap: wrap;
  gap: 8px;
}

.panel-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.panel-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: $blue;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}

.panel-title {
  font-size: 14px;
  font-weight: 700;
  color: $dark;
}

.panel-sub {
  font-size: 11px;
  color: #8a94a6;
}

.panel-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.btn-back {
  height: 32px;
  padding: 0 14px;
  border: 1.5px solid $border;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  font-family: $font;
  color: #4a5568;
  background: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: border-color 0.15s, color 0.15s;

  &:hover { border-color: $blue; color: $blue; }
}

.btn-save-alt {
  height: 32px;
  padding: 0 14px;
  border: 1.5px solid $blue;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  font-family: $font;
  color: $blue;
  background: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s, color 0.15s;

  &:hover { background: $blue; color: #fff; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}

.btn-save {
  height: 32px;
  padding: 0 14px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  font-family: $font;
  color: #fff;
  background: $green;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s;

  &:hover { background: #1d5230; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}

/* ── Two-column body ── */
.form-body {
  display: flex;
  align-items: flex-start;
  gap: 0;
}

.form-col {
  flex: 1;
  min-width: 0;
  padding: 0;

  &.form-col-right {
    border-left: 1px solid #eef0f6;
  }
}

/* ── Section heads ── */
.section-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: $blue;
  background: #f7f9fc;
  border-bottom: 1px solid #eef0f6;
  border-top: 1px solid #eef0f6;

  &.section-head-full { border-left: none; }

  .svc-error {
    margin-left: auto;
    color: $error;
    font-size: 11px;
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0;
  }
}

.fields-block {
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* ── Form fields ── */
.field-row {
  display: flex;
  gap: 12px;

  .field-group { flex: 1; }
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: $label-color;
  margin: 0;

  .req { color: $error; margin-left: 2px; }
}

.field-input {
  height: 40px;
  border: 1.5px solid $border;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 13px;
  color: $dark;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  font-family: $font;
  transition: border-color 0.2s, box-shadow 0.2s;

  &::placeholder { color: #b0bac9; }
  &:focus { border-color: $blue; box-shadow: 0 0 0 3px rgba(56,154,212,0.1); }
  &.has-error { border-color: $error; }

  &.field-textarea {
    height: auto;
    padding: 10px 12px;
    resize: vertical;
    line-height: 1.5;
  }
}

.field-select {
  height: 40px;
  border: 1.5px solid $border;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 13px;
  color: $dark;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  background: #fff;
  font-family: $font;
  cursor: pointer;
  transition: border-color 0.2s;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23a0aab8'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;

  &:focus { border-color: $blue; box-shadow: 0 0 0 3px rgba(56,154,212,0.1); }
  &.has-error { border-color: $error; }
}

.field-error {
  font-size: 11px;
  color: $error;
  margin-top: 2px;
}

/* ── Toggle grid ── */
.toggle-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 20px;
}

.toggle-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}

.toggle-label {
  font-size: 13px;
  color: #2d3748;
  font-weight: 500;
}

.toggle-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.toggle-input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;

  &:checked + .toggle-slider { background: $green; }
  &:checked + .toggle-slider::before { transform: translateX(16px); }
}

.toggle-slider {
  width: 36px;
  height: 20px;
  background: #cbd5e0;
  border-radius: 10px;
  position: relative;
  transition: background 0.2s;
  flex-shrink: 0;

  &::before {
    content: '';
    position: absolute;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #fff;
    top: 3px;
    left: 3px;
    transition: transform 0.2s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.15);
  }
}

/* ── Image upload ── */
.upload-area {
  border: 2px dashed $border;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  background: #fafbfd;
  transition: border-color 0.2s;

  &:hover { border-color: $blue; }
  &.has-image { border-style: solid; border-color: #c6dff5; background: #f0f8ff; }
}

.upload-preview {
  max-width: 120px;
  border-radius: 6px;
  border: 1px solid $border;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #a0aab8;

  i { font-size: 28px; }
  span { font-size: 13px; }
}

.upload-btn {
  height: 34px;
  padding: 0 16px;
  border: 1.5px solid $blue;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  font-family: $font;
  color: $blue;
  background: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s, color 0.15s;
  user-select: none;

  input[type="file"] { display: none; }
  &:hover { background: $blue; color: #fff; }
}

/* ── Map ── */
.map-wrap {
  border-radius: 8px;
  overflow: hidden;
  border: 1.5px solid $border;
}

/* ── Service table ── */
.svc-table-wrap {
  overflow-x: auto;
}

.svc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  font-family: $font;

  th, td {
    padding: 6px 10px;
    border-bottom: 1px solid #f0f2f8;
    vertical-align: middle;
    white-space: nowrap;
  }

  .svc-group-row th {
    background: #f0f4fa;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #6b7280;
    border-bottom: 1px solid #dde4f5;
  }

  .group-header {
    text-align: center;
    font-size: 10px;
    letter-spacing: 0.5px;
    font-weight: 700;
    text-transform: uppercase;
    border-left: 2px solid #dde4f5;

    &.default-group { background: #f0f4fa; color: #4a5568; }
    &.custom-group  { background: #f0fdf4; color: $green; }
  }

  .svc-th {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    text-align: right;
    border-bottom: 2px solid #dde4f5;

    &.default-col { background: #f7f9fc; color: #4a5568; }
    &.custom-col  { background: #f7fef9; color: $green; }
  }

  .svc-name {
    font-weight: 500;
    color: $dark;
    white-space: normal;
    max-width: 200px;
  }

  .svc-default {
    text-align: right;
    color: #718096;
    background: #fafbfd;
  }

  .svc-row-checked {
    background: #f0fdf4;
  }

  tbody tr:hover { background: #f8fbff; }
  tbody .svc-row-checked:hover { background: #e6fbf0; }
}

.svc-input {
  width: 80px;
  height: 30px;
  border: 1.5px solid $border;
  border-radius: 5px;
  padding: 0 6px;
  font-size: 12px;
  font-family: $font;
  color: $dark;
  text-align: right;
  outline: none;
  -moz-appearance: textfield;
  appearance: textfield;

  &:focus { border-color: $blue; }
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button { -webkit-appearance: none; appearance: none; margin: 0; }
}

.svc-checkbox {
  width: 15px;
  height: 15px;
  cursor: pointer;
  accent-color: $green;
}

.no-service {
  padding: 24px 20px;
  font-size: 13px;
  color: #a0aab8;
  display: flex;
  align-items: center;
  gap: 8px;
  border-top: 1px solid #eef0f6;
}
</style>
